import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "bootstrap-icons/font/bootstrap-icons.css";
import { leaveApi } from "../../../api/leaveApi";
import AllocateLeaveModal from "./AllocateLeaveModal";
import { formatPlainDate } from "../../../utils/dateTime";
import usePermissions from "../../../hooks/usePermissions";

const EmployeeLeaveAllocationCard = (props) => {
  const { id: propId, onRefresh } = props;
  const { id: routeId } = useParams();
  const id = propId || routeId;
  // Branch-scope roles see balances only; allocating stays company-wide HR
  const canAllocate = usePermissions().companyWide("leave", "modify");
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const handleOpen = (leave) => {
    setSelectedLeave(leave);
    setShowModal(true);
  };

  const { data: leaveList = [] } = useQuery({
    queryKey: ["employeeLeaveBalances", id],
    queryFn: async () => {
      // Current anniversary cycles (containing today)
      const response = await leaveApi.getLeaveBalanceEmployee(id);
      return response || [];
    },
    enabled: !!id && id !== "undefined",
  });

  useEffect(() => {
    if (!id || id === "undefined") {
      console.warn("Attempted to fetch leaves, but employeeId is missing!");
    }
  }, [id]);

  return (
    <div className="container-fluid px-0">
      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4 section-bar">
          <div className="d-flex align-items-center">
            <i className="bi bi-person-vcard-fill text-white me-2 fs-090"></i>
            <h6 className="m-0 text-white text-uppercase fs-075">
              Leave Allocations
            </h6>
          </div>
        </div>

        <div className="row g-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle shadow-sm bg-white">
              <thead className="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th>Leave Policy</th>
                  <th>Total</th>
                  <th>Booked</th>
                  <th>Used</th>
                  <th>Carried Over</th>
                  <th>Pending</th>
                  <th>Available</th>
                  <th>Cycle</th>
                  {canAllocate && <th className="text-center">Action</th>}
                </tr>
              </thead>
              <tbody>
                {leaveList.map((leave) => {
                  const allocated = parseFloat(leave.allocated) || 0;
                  const carriedOver = parseFloat(leave.carriedOver) || 0;
                  const used = parseFloat(leave.used) || 0;
                  const pending = Math.abs(parseFloat(leave.pending) || 0);
                  const available = allocated + carriedOver - used - pending;

                  return (
                    <tr key={leave.id}>
                      <td className="fw-bold">
                        {leave.leaveType?.name || "N/A"}
                      </td>
                      <td>{leave.leavePolicy?.name || "N/A"}</td>
                      <td>{leave.allocated}</td>
                      <td>{leave.booked}</td>
                      <td>{leave.used}</td>
                      <td>{leave.carriedOver}</td>
                      <td>{leave.pending}</td>
                      <td className="text-primary fw-bold">
                        {available.toFixed(1)}
                      </td>
                      <td className="text-nowrap fs-075">
                        {leave.periodStart
                          ? `${formatPlainDate(leave.periodStart)} – ${formatPlainDate(leave.periodEnd)}`
                          : leave.year}
                      </td>
                      {canAllocate && (
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary fw-bold fs-075 rounded-6"
                            onClick={() => handleOpen(leave)}
                            type="button"
                          >
                            Allocate
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AllocateLeaveModal
        isOpen={canAllocate && showModal}
        onClose={() => {
          setShowModal(false);
          if (onRefresh) onRefresh();
        }}
        leaveData={selectedLeave}
        onOpenModal={handleOpen}
      />
    </div>
  );
};

export default EmployeeLeaveAllocationCard;
